import { useEffect, useRef, useState } from 'react'
import Constants from '../../../app/utils/constants'

const orangeHeadingStyle = {
  background: 'linear-gradient(92.21deg, #EA892A 0%, #E35384 100%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
}

const getTwoLineTitle = (title: string) => {
  const words = title.trim().split(/\s+/)

  if (words.length <= 1) return [title, '']

  const splitAt = Math.ceil(words.length / 2)

  return [
    words.slice(0, splitAt).join(' '),
    words.slice(splitAt).join(' '),
  ]
}

const HealthcareDetails = () => {
  const { healthcareDetailsItems } = Constants

  const refs = useRef<(HTMLDivElement | null)[]>([])

  const [visible, setVisible] = useState<boolean[]>(
    () => new Array(healthcareDetailsItems.length).fill(false)
  )

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = Number(
            entry.target.getAttribute('data-index')
          )

          setVisible((prev) => {
            if (prev[index] === entry.isIntersecting)
              return prev

            const updated = [...prev]

            updated[index] = entry.isIntersecting

            return updated
          })
        })
      },
      {
        threshold: 0.25,
      }
    )

    refs.current.forEach((el) => el && observer.observe(el))

    return () => observer.disconnect()
  }, [healthcareDetailsItems.length])

  return (
    <section className="mx-auto w-full max-w-[1900px] px-4 py-10 md:px-8 md:py-12 lg:px-12 lg:py-14 xl:px-16 2xl:px-24">

      {/* Animation CSS */}
      <style>{`
        .slide-left {
          opacity: 0;
          transform: translateX(-10px);
          transition: opacity 0.5s ease, transform 0.9s ease-in;
        }

        .slide-right {
          opacity: 0;
          transform: translateX(10px);
          transition: opacity 0.5s ease, transform 0.9s ease-in;
        }

        .show {
          opacity: 1 !important;
          transform: translateX(0) !important;
        }

        .pulse {
          display: inline-block;
          transform-origin: center;
          animation: pulseScale 2s ease-in infinite;
          will-change: transform;
        }

        @keyframes pulseScale {
          0%, 100% {
            transform: scale(1);
          }

          50% {
            transform: scale(1.02);
          }
        }
      `}</style>

      {/* Heading */}
      <div className="mx-auto max-w-5xl text-center">
        <h2 className="font-rajdhani text-3xl font-semibold text-white sm:text-4xl md:text-4xl lg:text-5xl xl:text-6xl">
          What We Build for Healthcare
        </h2>
      </div>

      {/* Content */}
      <div className="mt-8 space-y-8 md:mt-10 md:space-y-10">

        {healthcareDetailsItems.map((item, index) => {
          const isImageLeft = index % 2 === 0

          const [titleLine1, titleLine2] =
            getTwoLineTitle(item.title)

          const headingStyle = isImageLeft
            ? orangeHeadingStyle
            : undefined

          const headingClass = isImageLeft
            ? 'font-rajdhani text-lg font-gilroy-bold sm:text-2xl md:text-2xl lg:text-2xl xl:text-3xl 2xl:text-4xl'
            : 'font-rajdhani text-lg font-gilroy-bold text-purple sm:text-2xl md:text-2xl lg:text-2xl xl:text-3xl 2xl:text-4xl'

          const gridCols = isImageLeft
            ? 'sm:grid-cols-[1fr_2fr]'
            : 'sm:grid-cols-[2fr_1fr]'

          const imageColClass = isImageLeft
            ? 'sm:col-start-1'
            : 'sm:col-start-2'

          const textColClass = isImageLeft
            ? 'sm:col-start-2'
            : 'sm:col-start-1'

          const slideClass = isImageLeft
            ? 'slide-left'
            : 'slide-right'

          return (
            <article
              key={item.title}
              ref={(el: HTMLDivElement | null) => {
                refs.current[index] = el
              }}
              data-index={index}
              className={`
                grid grid-cols-2 items-center gap-3
                sm:gap-4
                md:gap-5
                lg:gap-6
                xl:gap-8
                ${gridCols}
                ${slideClass}
                ${visible[index] ? 'show' : ''}
              `}
            >

              {/* IMAGE */}
              <div
                className={`
                  flex w-full justify-center
                  ${imageColClass}
                  ${!isImageLeft ? 'sm:order-2 order-2' : 'order-1'}
                `}
              >
                <div className="w-full overflow-hidden rounded-2xl">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="
                      h-[160px]
                      w-full
                      object-contain
                      sm:h-[220px]
                      md:h-[320px]
                      lg:h-[400px]
                      xl:h-[450px]
                      2xl:h-[450px]
                    "
                  />
                </div>
              </div>

              {/* TEXT */}
              <div
                className={`
                  flex flex-col justify-center
                  ${isImageLeft ? 'text-left pl-2' : 'text-right pr-2'}
                  sm:text-left
                  md:pl-[4%]
                  ${textColClass}
                  ${!isImageLeft ? 'sm:order-1 order-1' : 'order-2'}
                `}
              >
                <h3
                  className={`${headingClass} pulse`}
                  style={headingStyle}         
                >
                  <span className="block">
                    {titleLine1}
                  </span>

                  <span className="block -mt-2 md:mt-0">
                    {titleLine2 || '\u00A0'}
                  </span>
                </h3>

                <p
                  className="
                    mt-2
                    max-w-4xl
                    font-gilroy-medium
                    text-[11px]
                    leading-relaxed
                    text-white/70
                    sm:text-sm
                    md:text-lg
                    lg:text-lg
                    xl:text-xl
                    2xl:text-2xl
                  "
                >
                  {item.description}
                </p>
              </div>
            </article>
          )
        })}
      </div>
    </section>
  )
}

export default HealthcareDetails